import classNames from 'classnames';

import {
  Component,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  State
} from '@stencil/core';

type InternalStateType = {
  current?: number;
  pageSize?: number;
};

function isInteger(v) {
  const value = Number(v);
  return (
    typeof value === 'number' &&
    !isNaN(value) &&
    isFinite(value) &&
    Math.floor(value) === value
  );
}

function calculatePage(p, state, props) {
  const pageSize = typeof p === 'undefined' ? state?.pageSize : p;
  return Math.floor((props?.total - 1) / pageSize) + 1;
}

//@ts-ignore
function defaultItemRender(page, type, element) {
  return element;
}

function cloneElement(element, attributes) {
  const cln = Object.assign({}, element);
  Object.keys(attributes).forEach(key => {
    cln.$attrs$[key] = attributes[key];
  });
  return cln;
}

@Component({
  tag: 'uui-pagination',
  styleUrl: 'uui-pagination.scss',
  shadow: true
})

/**
 * @slot default - Slot for the content of the uui-pagination.
 */
export class UuiPagination {
  @State() state: InternalStateType = {};

  @Prop() disabled: boolean = false;
  @Prop({ attribute: 'default-current' }) defaultCurrent: number = 1;
  @Prop() current: number;
  @Prop() total: number = 0;
  @Prop({ attribute: 'default-page-size' }) defaultPageSize: number = 10;
  @Prop() pageSize: number;
  @Prop() class: string = '';
  @Prop() prefixCls: string = 'uui-pagination';
  @Prop({ attribute: 'hide-on-single-page' }) hideOnSinglePage: boolean = false;
  @Prop({ attribute: 'jumpers' })
  showPrevNextJumpers: boolean = true;
  @Prop({ attribute: 'less' }) showLessItems: boolean = false;
  @Prop({ attribute: 'show-title' }) showTitle: boolean = false;

  @Event({ bubbles: true, composed: true })
  changed: EventEmitter<any>;

  componentWillLoad() {
    this.first = this.first.bind(this);
    this.last = this.last.bind(this);
    this.prev = this.prev.bind(this);
    this.next = this.next.bind(this);
    this.jumpPrev = this.jumpPrev.bind(this);
    this.jumpNext = this.jumpNext.bind(this);

    this.handleChange = this.handleChange.bind(this);

    let current = this.defaultCurrent;
    if (this.current) {
      current = this.current;
    }
    let pageSize = this.defaultPageSize;
    if (this.pageSize) {
      pageSize = this.pageSize;
    }

    current = Math.min(current, calculatePage(pageSize, undefined, this));

    this.state = {
      current,
      pageSize
    };
  }

  componentWillRender() {
    if (this.current && this.current != this.state.current) {
      this.state = {
        ...this.state,
        current: this.current
      };
    }
    if (this.current && this.pageSize != this.state.pageSize) {
      this.state = {
        ...this.state,
        pageSize: this.pageSize
      };
    }
  }

  isValid(page) {
    const { total } = this;
    return (
      isInteger(page) &&
      page !== this.state.current &&
      isInteger(total) &&
      total > 0
    );
  }

  handleChange(p) {
    const { disabled } = this;

    let page = p;
    if (this.isValid(page) && !disabled) {
      const current = calculatePage(undefined, this.state, this);
      if (page > current) {
        page = current;
      } else if (page < 1) {
        page = 1;
      }

      if (!this.current) {
        this.state = {
          ...this.state,
          current: page
        };
      }

      const { pageSize } = this.state;
      this.changed.emit({
        page,
        pageSize
      });
      return page;
    }

    return this.state.current;
  }

  hasPrev() {
    return this.state.current > 1;
  }

  hasNext() {
    return this.state.current < calculatePage(undefined, this.state, this);
  }

  prev() {
    if (this.hasPrev()) {
      this.handleChange(this.state.current - 1);
    }
  }

  next() {
    if (this.hasNext()) {
      this.handleChange(this.state.current + 1);
    }
  }

  first() {
    this.handleChange(1);
  }

  last() {
    this.handleChange(calculatePage(undefined, this.state, this));
  }

  jumpPrev() {
    this.handleChange(this.getJumpPrevPage());
  }

  jumpNext() {
    this.handleChange(this.getJumpNextPage());
  }

  getJumpPrevPage() {
    return Math.max(1, this.state.current - (this.showLessItems ? 3 : 5));
  }

  getItemIcon(className, label, content = '') {
    const { prefixCls } = this;
    let iconNode = (
      <button
        type="button"
        aria-label={label}
        class={classNames('uui-button-no-styles', {
          [`${prefixCls}-item-link`]: true,
          [`${prefixCls}-button-${className}`]: true
        })}
      >
        {content}
      </button>
    );
    return iconNode;
  }

  getJumpNextPage() {
    return Math.min(
      calculatePage(undefined, this.state, this),
      this.state.current + (this.showLessItems ? 3 : 5)
    );
  }

  renderPrev() {
    return this.getItemIcon('left', 'Previous Page');
  }

  renderNext() {
    return this.getItemIcon('right', 'Next Page');
  }

  renderFirst() {
    const element = this.getItemIcon('first', 'First', 'First');
    element.$tag$ = 'a';
    return element;
  }

  renderLast() {
    const element = this.getItemIcon('last', 'Last', 'Last');
    element.$tag$ = 'a';
    return element;
  }

  renderPager({
    page,
    active,
    className,
    onClick,
    showTitle
  }: {
    page?: number;
    active?: boolean;
    className?: string;
    onClick?: (page: number) => void;
    showTitle?: boolean;
  }) {
    const prefixCls = `${this.prefixCls}-item`;
    const cls = classNames(prefixCls, `${prefixCls}-${page}`, {
      [`${prefixCls}-active`]: active,
      [`${prefixCls}-disabled`]: !page,
      [className]: !!className
    });

    const handleClick = () => {
      onClick(page);
    };

    return (
      <li
        //@ts-ignore
        title={showTitle ? page : null}
        class={cls}
        onClick={handleClick}
        tabIndex={0}
      >
        {defaultItemRender(page, 'page', <a rel="nofollow">{page}</a>)}
      </li>
    );
  }

  render() {
    const {
      class: className,
      total,
      prefixCls,
      showTitle,
      showLessItems,
      showPrevNextJumpers,
      hideOnSinglePage
    } = this;
    const { current, pageSize } = this.state;
    if (hideOnSinglePage === true && total <= pageSize) {
      return null;
    }

    const allPages = calculatePage(undefined, this.state, this);
    const pagerList = [];
    let jumpPrev = null;
    let jumpNext = null;
    let firstPager = null;
    let lastPager = null;

    const pageBufferSize = showLessItems ? 1 : 2;

    if (allPages <= 3 + pageBufferSize * 2) {
      const pagerProps = {
        rootPrefixCls: prefixCls,
        onClick: this.handleChange,
        showTitle
      };
      if (!allPages) {
        pagerList.push(
          this.renderPager({
            ...pagerProps,
            page: 1,
            className: `${prefixCls}-item-disabled`
          })
        );
      }
      for (let i = 1; i <= allPages; i += 1) {
        const active = current === i;
        pagerList.push(
          this.renderPager({
            ...pagerProps,
            page: i,
            active,
            className: `${prefixCls}-item-disabled`
          })
        );
      }
    } else {
      const prevItemTitle = showLessItems
        ? 'Previous 3 Pages'
        : 'Previous 5 Pages';
      const nextItemTitle = showLessItems ? 'Next 3 Pages' : 'Next 5 Pages';
      if (showPrevNextJumpers) {
        jumpPrev = (
          <li
            title={showTitle ? prevItemTitle : null}
            key="prev"
            onClick={this.jumpPrev}
            tabIndex={0}
            class={`${prefixCls}-jump-prev`}
          >
            {defaultItemRender(
              this.getJumpPrevPage(),
              'jump-prev',
              this.getItemIcon('jump-prev-icon', 'prev page')
            )}
          </li>
        );
        jumpNext = (
          <li
            title={showTitle ? nextItemTitle : null}
            key="next"
            tabIndex={0}
            onClick={this.jumpNext}
            class={`${prefixCls}-jump-next`}
          >
            {defaultItemRender(
              this.getJumpNextPage(),
              'jump-next',
              this.getItemIcon('jump-next-icon', 'next page')
            )}
          </li>
        );
      }
      lastPager = this.renderPager({
        onClick: this.handleChange,
        page: allPages,
        active: false,
        showTitle,
        className: `${prefixCls}-item-disabled`
      });

      firstPager = this.renderPager({
        onClick: this.handleChange,
        page: 1,
        active: false,
        showTitle
      });

      let left = Math.max(1, current - pageBufferSize);
      let right = Math.min(current + pageBufferSize, allPages);

      if (current - 1 <= pageBufferSize) {
        right = 1 + pageBufferSize * 2;
      }

      if (allPages - current <= pageBufferSize) {
        left = allPages - pageBufferSize * 2;
      }

      for (let i = left; i <= right; i += 1) {
        const active = current === i;
        pagerList.push(
          this.renderPager({
            onClick: this.handleChange,
            page: i,
            active,
            showTitle
          })
        );
      }

      if (current - 1 >= pageBufferSize * 2 && current !== 1 + 2) {
        pagerList[0] = cloneElement(pagerList[0], {
          class: `${pagerList[0].$attrs$.class} ${prefixCls}-item-after-jump-prev`
        });
        pagerList.unshift(jumpPrev);
      }
      if (
        allPages - current >= pageBufferSize * 2 &&
        current !== allPages - 2
      ) {
        pagerList[pagerList.length - 1] = cloneElement(
          pagerList[pagerList.length - 1],
          {
            class: `${
              pagerList[pagerList.length - 1].$attrs$.class
            } ${prefixCls}-item-before-jump-next`
          }
        );
        pagerList.push(jumpNext);
      }

      if (left !== 1) {
        pagerList.unshift(firstPager);
      }
      if (right !== allPages) {
        pagerList.push(lastPager);
      }
    }

    const prevDisabled = !this.hasPrev() || !allPages;
    const nextDisabled = !this.hasNext() || !allPages;

    return (
      <Host>
        <ul class={className}>
          <li
            title={showTitle ? 'Fist Page' : null}
            onClick={this.first}
            tabIndex={prevDisabled ? null : 0}
            class={classNames(`first`, 'uui-pagination-item', {
              [`disabled`]: prevDisabled,
              [`${prefixCls}-first`]: true
            })}
            aria-disabled={prevDisabled}
          >
            {this.renderFirst()}
          </li>
          <li
            title={showTitle ? 'Previous Page' : null}
            onClick={this.prev}
            tabIndex={prevDisabled ? null : 0}
            class={classNames(`prev`, {
              [`disabled`]: prevDisabled,
              [`${prefixCls}-prev`]: true
            })}
            aria-disabled={prevDisabled}
          >
            {this.renderPrev()}
          </li>
          {pagerList}
          <li
            title={showTitle ? 'Next Page' : null}
            onClick={this.next}
            tabIndex={nextDisabled ? null : 0}
            class={classNames(`next`, {
              [`disabled`]: nextDisabled,
              [`${prefixCls}-next`]: true
            })}
            aria-disabled={nextDisabled}
          >
            {this.renderNext()}
          </li>
          <li
            title={showTitle ? 'Last Page' : null}
            onClick={this.last}
            tabIndex={nextDisabled ? null : 0}
            class={classNames(`last`, 'uui-pagination-item', {
              [`disabled`]: nextDisabled,
              [`${prefixCls}-last`]: true
            })}
            aria-disabled={nextDisabled}
          >
            {this.renderLast()}
          </li>
        </ul>
      </Host>
    );
  }
}
